import React, { useContext, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { logSentryError } from '@node-space/utils'
import { useCreateTransferMutation } from 'hooks/mutations/useCreateTransferMutation'
import { GetTransferPayloadProps, MovePayload, TransferStep } from 'pages/NestedCustomers/types'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BaseErrorResponse } from 'types/beneficiaries'
import { TransferRequest, WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { generateReference } from 'utils/utils'
import { transferSchema } from '../../../NestedCustomers/schema/transferSchema'
import { useSourceWallet } from './hooks/useSourceWallet'
import { MoveComplete } from './MoveComplete'
import { MoveConfirm } from './MoveConfirm'
import { MoveForm } from './MoveForm'

export const Move = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [transferStep, setTransferStep] = useState<TransferStep>(TransferStep.FORM)
  const [walletAction, setWalletAction] = useContext(WalletActionContext)
  const [transferError, setTransferError] = useState(null)
  const sourceWallet = useSourceWallet()
  const { onClose: handleClose } = walletAction || {}

  const { mutate: createTransferMutation, isPending: isLoadingCreateTransfer } =
    useCreateTransferMutation()

  const formSchema = transferSchema(t)
  const { control, formState, register, setValue, trigger, clearErrors, setError, watch } =
    useForm<MovePayload>({
      resolver: yupResolver(formSchema),
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        walletTransferType: WalletTransferType?.MAIN,
        customerReference: '',
        destinationWallet: '',
        amount: '',
        paymentReference: generateReference(),
      },
    })

  const formValues = watch()
  const [destinationWallet, amount, paymentReference] = useWatch({
    control,
    name: ['destinationWallet', 'amount', 'paymentReference'],
  })

  const handleOnBack = () => {
    setTransferStep(TransferStep.FORM)
  }

  const getTransferPayload = ({
    destinationWalletId,
    transferAmount,
    paymentReference,
  }: GetTransferPayloadProps): TransferRequest => {
    return {
      walletId: sourceWallet?.walletId,
      amount: transferAmount,
      currency: sourceWallet?.currency,
      reference: paymentReference,
      beneficiary: {
        walletId: destinationWalletId,
      },
    }
  }

  const handleSubmit = async () => {
    setTransferError(null)
    const payload = getTransferPayload({
      destinationWalletId: destinationWallet,
      transferAmount: amount,
      paymentReference: paymentReference,
    })
    createTransferMutation(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['transferWallets'] })
        setWalletAction({
          ...walletAction,
          header: null,
          onBack: null,
        })
        setTransferStep(TransferStep.COMPLETE)
      },
      onError: (error: BaseErrorResponse) => {
        const errorList = error?.errorList
        if (errorList?.length) {
          errorList?.forEach(err => {
            setError(err?.parameter as keyof MovePayload, { message: error?.message })
          })
        } else {
          setTransferError(t('customers.transferError'))
        }
        logSentryError('useCreateTransferMutation', error, payload)
      },
    })
  }

  return (
    <>
      {transferStep === TransferStep?.FORM && (
        <MoveForm
          setValue={setValue}
          handleClose={handleClose}
          register={register}
          trigger={trigger}
          formState={formState}
          formValues={formValues}
          setTransferStep={setTransferStep}
          setError={setError}
          clearErrors={clearErrors}
        />
      )}

      {transferStep === TransferStep?.CONFIRMATION && (
        <MoveConfirm
          formValues={formValues}
          isLoading={isLoadingCreateTransfer}
          transferError={transferError}
          handleSubmit={handleSubmit}
          onBack={handleOnBack}
        />
      )}

      {transferStep === TransferStep?.COMPLETE && (
        <MoveComplete formValues={formValues} transferError={transferError} />
      )}
    </>
  )
}
